import * as React from "react";

import { debounce, camelCase } from "lodash";
import { Box, Button, FormField, MaskedInput, Select, TextArea, TextInput, Text } from "grommet";
import { normalizeColor } from "grommet/utils";
import Theme from "../utils/Theme";
import { Question, Option, GetReferenceList } from "../utils/ServiceManagerAPI";
import { useDarkMode } from "./DarkModeSwitch";

const { Component } = React;

const AnswerableOption = ({ question, option, changed, value: propValue, atomic, disabled }: { question: Question; option: Option; changed: Function; value: any; atomic: boolean; disabled: boolean }) => {
    const [value, setValue] = React.useState("" as any);

    const [referenceList, setReferenceList] = React.useState([] as any[]);
    const [referenceValue, setReferenceValue] = React.useState({} as any);

    React.useEffect(() => {
        if (option.referenceListID) {
            GetReferenceList(option.referenceListID).then((res) => {
                setReferenceList(res.data.result.items);
                setReferenceValue(res.data.result.items.find((item) => item.name == value));
            });
        }
    }, [value]);

    const doChange = (v) => {
        doFocus(false);
        if (!changed(v)) {
            setValue(propValue);
        }
    };

    React.useEffect(() => {
        if (value != propValue) {
            let v = propValue;

            if (v === null) {
                v = "";
            }

            switch (v?.toString()?.toLowerCase()) {
                case "true":
                    v = true;
                    break;
                case "false":
                    v = false;
                    break;
                case null:
                    v = "";
                    break;
            }

            setValue(v);
        }
    }, [propValue]);

    const handleChange = (e) => {
        let v = e.target.value;
        if (option.type == 0) {
            v = !value;
        }

        setValue(v);
        return v;
    };

    const handleAtomicChange = (value) => (e) => {
        setValue(value);
        doChange(value);
    };

    const handleChangeAndUpdate = (e) => {
        let value = handleChange(e);
        doChange(value);
    };

    const handleReferenceChange = (value) => {
        setReferenceValue(value);
        doChange(value.name);
    };

    const [isDarkMode] = useDarkMode();

    const inputStyle = { backgroundColor: normalizeColor("background-back", Theme, isDarkMode) };

    const buttonStyle = {
        color: `var(${isDarkMode ? "--data-color-dark" : "--data-color-light"},${isDarkMode ? "var(--text-color-dark)" : "var(--text-color-light)"})`,
    };

    const [focus, setFocus] = React.useState(false);

    const doFocus = (focus) => {
        setFocus(focus);
    };

    React.useEffect(() => {
        if (focus) {
            window.onbeforeunload = function (evt) {
                if (propValue != value) {
                    changed(value);
                }
            };
        } else {
            window.onbeforeunload = null;
        }
    }, [focus, value, propValue]);

    switch (option.type) {
        case 0: {
            return (
                <>
                    {atomic && (
                        <Box gap="xsmall">
                            {option.text && <Text>{option.text}</Text>}
                            <Box gap="xsmall" direction="row">
                                <Button label="Yes" color={!disabled ? "brand" : "background-front"} style={buttonStyle} disabled={disabled} primary={value == true} onClick={handleAtomicChange(true)}></Button>
                                <Button label="No" color={!disabled ? "brand" : "background-front"} style={buttonStyle} disabled={disabled} primary={value == false} onClick={handleAtomicChange(false)}></Button>
                            </Box>
                        </Box>
                    )}
                    {!atomic && <Button style={{ marginBottom: "0.3rem", ...buttonStyle, backgroundColor: !!value ? "" : normalizeColor("background-back", Theme, isDarkMode) }} label={option.text} disabled={disabled} primary={!!value} color={!disabled ? "brand" : "background-front"} onClick={handleChangeAndUpdate}></Button>}
                </>
            );
        }
        case 1:
            return (
                <Box>
                    <FormField label={option.text && option.text} disabled={disabled}>
                        <TextInput onFocus={() => doFocus(true)} style={{ ...buttonStyle, ...inputStyle, width: "100%", minHeight: "44.67px", maxHeight: "150px", height: "34px" }} autoComplete={"nope"} name={camelCase(option.text)} value={value} onChange={handleChange} onBlur={() => doChange(value)} disabled={disabled} />
                    </FormField>
                </Box>
            );
        case 2:
            return (
                <Box>
                    <FormField label={option.text && option.text} disabled={disabled}>
                        <TextInput onFocus={() => doFocus(true)} style={{ ...buttonStyle, ...inputStyle }} type="number" autoComplete={"nope"} name={camelCase(option.text)} value={value} disabled={disabled} onChange={handleChange} onBlur={() => doChange(value)} />
                    </FormField>
                </Box>
            );
        case 4:
            return (
                <Box>
                    <FormField label={option.text && option.text} disabled={disabled}>
                        <TextInput onFocus={() => doFocus(true)} style={{ ...buttonStyle, ...inputStyle }} type="date" autoComplete={"nope"} name={camelCase(option.text)} value={value} disabled={disabled} onChange={handleChange} onBlur={() => doChange(value)} />
                    </FormField>
                </Box>
            );
        case 5:
            return (
                <Box>
                    <FormField label={option.text && option.text} disabled={disabled}>
                        <TextInput onFocus={() => doFocus(true)} style={{ ...buttonStyle, ...inputStyle }} type="time" autoComplete={"nope"} name={camelCase(option.text)} value={value} disabled={disabled} onChange={handleChange} onBlur={() => doChange(value)} />
                    </FormField>
                </Box>
            );
        case 6:
            return (
                <Box>
                    <FormField label={option.text && option.text} disabled={disabled}>
                        {!referenceList && <></>}
                        {referenceList && <Select style={{ ...buttonStyle, ...inputStyle }} disabled={disabled} options={referenceList} autoComplete={"nope"} name={camelCase(option.text)} value={referenceValue} valueKey="name" labelKey="name" onChange={({ value }) => handleReferenceChange(value)} />}
                    </FormField>
                </Box>
            );
        case 7:
            return (
                <Box>
                    <FormField label={option.text && option.text} disabled={disabled}>
                        <TextArea onFocus={() => doFocus(true)} style={{ ...buttonStyle, ...inputStyle, minHeight: "150px", maxHeight: "150px" }} autoComplete={"nope"} name={camelCase(option.text)} value={value} onChange={handleChange} onBlur={() => doChange(value)} disabled={disabled} size={"small"} />
                    </FormField>
                </Box>
            );
    }

    return <></>;
};

export default AnswerableOption;
